import { useState } from "react";
import {
  CheckCircle2,
  LoaderCircle,
  LockKeyhole,
} from "lucide-react";
import {
  Link,
  useParams,
} from "react-router-dom";

import { resetPassword } from "../../utils/auth";

function ResetPasswordPage() {
  const { uid, token } = useParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [complete, setComplete] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    if (!password || password.length < 8) {
      setError("Enter a new password with at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Both password fields must match.");
      return;
    }
    setIsSubmitting(true);
    setError("");
    try {
      await resetPassword({ uid, token, password });
      setComplete(true);
    } catch (requestError) {
      setError(requestError.message || "This reset link could not be used.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="sugalump-login">
      <section className="sugalump-login__visual">
        <div className="sugalump-login__visual-content">
          <div className="sugalump-login__brand-mark">SL</div>
          <p className="eyebrow">SugaLumps Cupcakery</p>
          <h1>Choose a new admin password.</h1>
          <p>Use your secure reset link to regain access to the bakery dashboard.</p>
          <div className="sugalump-login__decoration">
            <span />
            <strong>Secure reset</strong>
            <span />
          </div>
        </div>
      </section>

      <section className="sugalump-login__form-side">
        <div className="sugalump-login-card">
          <header className="sugalump-login-card__header">
            <div className="sugalump-login-card__mobile-logo">SL</div>
            <p className="eyebrow">Bakery administration</p>
            <h2>{complete ? "Password updated" : "Reset password"}</h2>
            <p>{complete ? "You can now sign in with your new password." : "Enter a new password for your SugaLumps admin account."}</p>
          </header>

          {error && (
            <div className="sugalump-login-error" role="alert">
              <LockKeyhole size={18} />
              <span>{error}</span>
            </div>
          )}

          {complete ? (
            <div className="sugalump-reset-success">
              <CheckCircle2 size={24} />
              <p>Your password has been reset.</p>
              <Link className="btn btn--primary sugalump-login-submit" to="/login">Back to Sign In</Link>
            </div>
          ) : (
            <form className="sugalump-login-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label" htmlFor="password">New password</label>
                <div className="sugalump-login-input">
                  <LockKeyhole size={18} />
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="confirmPassword">Confirm password</label>
                <div className="sugalump-login-input">
                  <LockKeyhole size={18} />
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <button type="submit" className="btn btn--primary sugalump-login-submit" disabled={isSubmitting}>
                {isSubmitting && <LoaderCircle size={18} className="admin-spin" />}
                {isSubmitting ? "Updating..." : "Set New Password"}
              </button>
            </form>
          )}

          <footer className="sugalump-login-card__footer">
            <p>Return to sign in after resetting your password.</p>
          </footer>
        </div>
      </section>
    </main>
  );
}

export default ResetPasswordPage;